import React from "react"
import PropTypes from "prop-types"
import Link from "../utils/Link"

import Box from "../atoms/Box"
import Button from "../atoms/Button"
import { Label, Paragraph } from "../atoms/Typography"
import Icon from "../atoms/Icon"
import FlexGrid from "../atoms/FlexGrid"
import NavLink from "../atoms/NavLink"
import RichText from "../atoms/RichText"

const getColumnCount = columnsOnGrid => {
  switch (columnsOnGrid) {
    case "two":
      return 2
    case "three":
      return 3
    case "four":
      return 4
    case "five":
      return 5
    default:
      return 3
  }
}

// This component renders all columns inside the mega menu
export const MegaMenuContent = ({
  megaMenuColumns = [],
  showDescription,
  showIcon,
  iconSize,
  iconLocation,
  columnSpacing,
  forceOpen,
  columnsOnGrid = "three",
}) => {
  const columnCount = getColumnCount(columnsOnGrid)

  // Helper function to render each link item
  const renderMegaLinkItem = ({ megaLink, index, columnsLinks }) => {
    const hasIcon = showIcon && megaLink?.iconAttribute?.image?.url
    const isIconTop = iconLocation === "top"
    const isIconBottom = iconLocation === "bottom"

    return (
      <Box
        key={megaLink.strapi_id || index}
        mb={columnsLinks?.length === 1 ? 0 : 5}
        width="100%"
      >
        <Box
          display="flex"
          flexDirection={
            isIconBottom ? "column-reverse" : isIconTop ? "column" : "row"
          }
          alignItems="flex-start"
          flexWrap={isIconBottom || isIconTop ? "wrap" : "nowrap"}
        >
          {hasIcon && (
            <Box
              display="flex"
              width={iconLocation === "left" ? "100%" : "100%"}
              maxWidth={`${iconSize}px`}
              height="100%"
              mr={isIconTop ? 0 : 3}
              mt={isIconBottom ? 3 : 0}
            >
              <Icon
                url={megaLink.iconAttribute.image.url}
                alternativeText={
                  megaLink.iconAttribute.image?.alternativeText || ""
                }
                width="100%"
                height="auto"
              />
            </Box>
          )}

          <Box
            display="flex"
            flexDirection="column"
            flex={iconLocation === "left" ? 1 : null}
          >
            {(megaLink.link || megaLink.name) && (
              <Link
                aria-label="Mega menu link"
                {...megaLink.link}
                onClick={e => {
                  if (forceOpen) {
                    e.preventDefault()
                    e.stopPropagation()
                  }
                }}
              >
                <NavLink
                  aria-label="Mega menu nav link"
                  linkText={megaLink?.link?.linkText || megaLink.name}
                  variant="MegaMenuLinkTitle"
                  style={{ cursor: "pointer" }}
                />
              </Link>
            )}

            {megaLink.descriptionRichText?.content && showDescription && (
              <RichText
                mt={1}
                mb={-4}
                textAlign="left"
                variant="MegaMenuDescription"
                {...megaLink.descriptionRichText}
              />
            )}
            {megaLink.button?.text && !isIconBottom && (
              <Box mt={3}>
                <Button
                  aria-label="Mega menu link button"
                  {...megaLink.button}
                  onClick={e => {
                    if (forceOpen) {
                      e.preventDefault()
                      e.stopPropagation()
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        {megaLink.button?.text && isIconBottom && (
          <Box mt={3}>
            <Button
              aria-label="Mega menu link button"
              {...megaLink.button}
              onClick={e => {
                if (forceOpen) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }}
            />
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box p={{ _: 5, md: 7 }}>
      <FlexGrid
        columns={{ _: 1, md: columnCount }}
        gutter={{ _: "0px", md: `${columnSpacing}px` }}
        align="left"
        maxItemWidth="none"
      >
        {megaMenuColumns.map((col, colIndex) => (
          <Box
            key={col.strapi_id || colIndex}
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            {col.linkTitle?.length > 0 && (
              <Box mb={3}>
                <Label mb={1} variant="MegaMenuColumnTitle">
                  {col.linkTitle}
                </Label>
              </Box>
            )}

            {col.megaMenuLinks?.map((megaLink, index) =>
              renderMegaLinkItem({
                columnsLinks: col.megaMenuLinks,
                megaLink,
                index,
              })
            )}
          </Box>
        ))}
      </FlexGrid>
    </Box>
  )
}

MegaMenuContent.propTypes = {
  megaMenuColumns: PropTypes.array,
  showDescription: PropTypes.bool,
  showIcon: PropTypes.bool,
  iconSize: PropTypes.number,
  iconLocation: PropTypes.oneOf(["top", "bottom", "left"]),
  columnSpacing: PropTypes.number,
  forceOpen: PropTypes.bool,
  columnsOnGrid: PropTypes.oneOf(["two", "three", "four", "five"]),
}
